import React from 'react';
import { CanvasDimensions } from './useResponsiveCanvas';

interface PrologueSceneProps {
  dimensions: CanvasDimensions;
}

export const PrologueDeparture: React.FC<PrologueSceneProps> = React.memo(({ dimensions }) => {
  const { vbWidth, vbHeight, isPortrait } = dimensions;
  const cx = vbWidth / 2;
  const cliffY = vbHeight * 0.68;
  const sunX = Math.min(vbWidth - 100, cx + (isPortrait ? 110 : 250));
  const sunY = vbHeight * 0.36;
  const heroX = isPortrait ? cx - 110 : cx - 200;

  return (
    <svg
      className="w-full h-full absolute inset-0 select-none pointer-events-none"
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Masterpiece Alpine Sunrise Sky */}
        <linearGradient id="dawnSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#181336" />
          <stop offset="25%" stopColor="#31104e" />
          <stop offset="50%" stopColor="#6b1248" />
          <stop offset="70%" stopColor="#a31644" />
          <stop offset="85%" stopColor="#ea580c" />
          <stop offset="95%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#fef9c3" />
        </linearGradient>

        {/* Dawn Sun Radial Ambient Glare */}
        <radialGradient id="sunDawnGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="20%" stopColor="#fef08a" stopOpacity="0.85" />
          <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.45" />
          <stop offset="75%" stopColor="#ea580c" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#9f1239" stopOpacity="0" />
        </radialGradient>

        {/* Soft Sun Core without Hard Ring */}
        <radialGradient id="sunCoreGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="50%" stopColor="#fffbeb" stopOpacity="0.95" />
          <stop offset="80%" stopColor="#fef08a" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#fb923c" stopOpacity="0.2" />
        </radialGradient>

        {/* Mountain Ridge Layers with Atmospheric Sunrise Silhouette Light */}
        <linearGradient id="dawnMtnFar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#581c87" />
          <stop offset="60%" stopColor="#2e1065" />
          <stop offset="100%" stopColor="#1e1b4b" />
        </linearGradient>
        <linearGradient id="dawnMtnMid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#701a75" />
          <stop offset="50%" stopColor="#4a044e" />
          <stop offset="100%" stopColor="#240c38" />
        </linearGradient>
        <linearGradient id="dawnMtnRidge" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#831843" />
          <stop offset="60%" stopColor="#500724" />
          <stop offset="100%" stopColor="#1a0624" />
        </linearGradient>

        {/* Village Terraces and Terracotta Roofs */}
        <linearGradient id="villageRoofGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fb923c" />
          <stop offset="50%" stopColor="#ea580c" />
          <stop offset="100%" stopColor="#9a3412" />
        </linearGradient>
        <linearGradient id="villageWallGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6c1d48" />
          <stop offset="50%" stopColor="#501235" />
          <stop offset="100%" stopColor="#380a24" />
        </linearGradient>

        {/* Foreground Crag / Mineral Cliff Stone */}
        <linearGradient id="cliffStoneGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#292524" />
          <stop offset="35%" stopColor="#1c1917" />
          <stop offset="100%" stopColor="#0c0a09" />
        </linearGradient>

        {/* Cliff Edge Dawn Sunlight Rim */}
        <linearGradient id="cliffDawnRimGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ea580c" stopOpacity="0.3" />
          <stop offset="40%" stopColor="#fde047" stopOpacity="0.85" />
          <stop offset="85%" stopColor="#fef08a" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#f97316" stopOpacity="0.4" />
        </linearGradient>

        {/* Hero Amulet Morning Gleam */}
        <radialGradient id="amuletHeroGleam" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="35%" stopColor="#38bdf8" stopOpacity="0.9" />
          <stop offset="75%" stopColor="#0284c7" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#0369a1" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* 1. SKY & SUNRISE COLORSCAPE */}
      <rect x="0" y="0" width={vbWidth} height={vbHeight} fill="url(#dawnSky)" />

      {/* The Rising Sun Disk & Natural Atmospheric Aura (Smooth Luminous Dawn Core) */}
      <circle cx={sunX} cy={sunY} r={175} fill="url(#sunDawnGlow)" />
      <circle cx={sunX} cy={sunY} r={34} fill="url(#sunCoreGrad)" />
      <circle cx={sunX} cy={sunY} r={16} fill="#ffffff" />

      {/* 2. DISTANT MOUNTAIN RANGES (Djurdjura receding silhouette layers) */}
      {/* Mountain Range 1 (Far High Crests) */}
      <polygon
        points={`-100,${cliffY - 30} 
                 ${sunX * 0.25},${cliffY - 180} 
                 ${sunX * 0.65},${cliffY - 215} 
                 ${sunX},${cliffY - 145} 
                 ${sunX * 1.3},${cliffY - 195} 
                 ${vbWidth + 100},${cliffY - 120} 
                 ${vbWidth + 100},${cliffY + 100} 
                 -100,${cliffY + 100}`}
        fill="url(#dawnMtnFar)"
      />

      {/* Mountain Range 2 (Mid-distance Ridge) */}
      <polygon
        points={`-100,${cliffY} 
                 ${cx * 0.35},${cliffY - 115} 
                 ${cx * 0.75},${cliffY - 145} 
                 ${cx * 1.15},${cliffY - 95} 
                 ${cx * 1.55},${cliffY - 130} 
                 ${vbWidth + 100},${cliffY - 110} 
                 ${vbWidth + 100},${cliffY + 140} 
                 -100,${cliffY + 140}`}
        fill="url(#dawnMtnMid)"
      />

      {/* Mountain Range 3 (Village Ridge Crest - Natural Perched Slope) */}
      <polygon
        points={`-100,${cliffY + 25} 
                 ${cx * 0.45},${cliffY - 45} 
                 ${cx * 0.85},${cliffY - 60} 
                 ${cx + (isPortrait ? 20 : 60)},${cliffY - 50} 
                 ${cx + (isPortrait ? 180 : 250)},${cliffY - 30} 
                 ${vbWidth + 100},${cliffY - 40} 
                 ${vbWidth + 100},${vbHeight + 50} 
                 -100,${vbHeight + 50}`}
        fill="url(#dawnMtnRidge)"
      />

      {/* 3. TRADITIONAL KABYLE VILLAGE (Taddart n Djurdjura) NATURALLY SITUATED ON THE RIDGE */}
      <g transform={`translate(${cx + (isPortrait ? 20 : 70)}, ${cliffY - 58})`}>
        {/* --- UPPER CREST TIER (Background Houses) --- */}
        {/* House A1 (Upper left) */}
        <g transform="translate(14, 2)">
          <rect x="0" y="4" width="18" height="13" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 9,-3 20,4" fill="url(#villageRoofGrad)" />
          <rect x="4" y="8" width="2.5" height="2.5" fill="#fef08a" opacity="0.85" />
        </g>

        {/* House A2 (Communal Tajmaɛt High House) */}
        <g transform="translate(38, -4)">
          <rect x="0" y="4" width="24" height="17" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 12,-4 26,4" fill="url(#villageRoofGrad)" />
          <rect x="5" y="8" width="3" height="3" fill="#fef08a" opacity="0.9" />
          <rect x="15" y="8" width="3" height="3" fill="#fef08a" opacity="0.7" />
          <rect x="10" y="13" width="4" height="8" fill="#380a24" />
        </g>

        {/* House A3 (Upper right) */}
        <g transform="translate(68, 1)">
          <rect x="0" y="4" width="20" height="14" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 10,-3 22,4" fill="url(#villageRoofGrad)" />
          <rect x="12" y="8" width="2.5" height="2.5" fill="#fef08a" opacity="0.8" />
        </g>

        {/* --- LOWER & FOREGROUND TIER (Cascading Slope Houses) --- */}
        {/* House B1 (Left flank) */}
        <g transform="translate(-10, 10)">
          <rect x="0" y="4" width="18" height="13" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 9,-2 20,4" fill="url(#villageRoofGrad)" />
          <rect x="5" y="8" width="2.5" height="2.5" fill="#fef08a" opacity="0.85" />
          <rect x="12" y="10" width="3.5" height="7" fill="#380a24" />
        </g>

        {/* House B2 (Central terraced house) */}
        <g transform="translate(94, 6)">
          <rect x="0" y="4" width="22" height="15" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 11,-3 24,4" fill="url(#villageRoofGrad)" />
          <rect x="5" y="8" width="3" height="3" fill="#fef08a" opacity="0.9" />
          <rect x="14" y="8" width="3" height="3" fill="#fef08a" opacity="0.75" />
        </g>

        {/* House B3 (Right mid slope) */}
        <g transform="translate(122, 11)">
          <rect x="0" y="4" width="18" height="14" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 9,-2 20,4" fill="url(#villageRoofGrad)" />
          <rect x="6" y="8" width="2.5" height="2.5" fill="#fef08a" opacity="0.8" />
          <rect x="12" y="10" width="3.5" height="8" fill="#380a24" />
        </g>

        {/* House B4 (Descent corner house) */}
        <g transform="translate(144, 16)">
          <rect x="0" y="4" width="16" height="12" fill="url(#villageWallGrad)" />
          <polygon points="-2,4 8,-2 18,4" fill="url(#villageRoofGrad)" />
          <rect x="5" y="8" width="2.5" height="2.5" fill="#fef08a" opacity="0.75" />
        </g>

        {/* --- PURE NATURAL MEDITERRANEAN SILHOUETTES --- */}
        {/* Left Olive Tree */}
        <g transform="translate(-20, 16)">
          <path
            d="M 5 12 L 5 6 C 2 5, -1 3, -2 -1 C -3 -5, 2 -8, 6 -7 C 9 -9, 15 -6, 14 -2 C 13 2, 10 5, 7 6 L 7 12 Z"
            fill="#380d28"
          />
        </g>

        {/* Center Olive Tree (Shifted slightly right in the lower meadow) */}
        <g transform="translate(32, 23)">
          <path
            d="M 5 12 L 5 6 C 2 5, 0 3, -1 0 C -2 -4, 3 -7, 7 -6 C 10 -8, 15 -5, 14 -1 C 13 3, 10 5, 7 6 L 7 12 Z"
            fill="#380d28"
          />
        </g>

        {/* Right Flank Olive Tree */}
        <g transform="translate(162, 20)">
          <path
            d="M 5 12 L 5 6 C 2 5, 0 3, -1 0 C -2 -4, 3 -7, 7 -6 C 10 -8, 15 -5, 14 -1 C 13 3, 10 5, 7 6 L 7 12 Z"
            fill="#380d28"
          />
        </g>
      </g>

      {/* 4. ETHEREAL VALLEY MIST & ATMOSPHERIC DAWN GLOW IN BACKGROUND BASIN */}
      <path
        d={`M -50 ${cliffY + 12} 
            Q ${cx * 0.45} ${cliffY - 16} ${cx * 0.95} ${cliffY + 12} 
            T ${vbWidth + 50} ${cliffY - 5} 
            L ${vbWidth + 50} ${cliffY + 70} 
            L -50 ${cliffY + 70} Z`}
        fill="#f43f5e"
        opacity="0.16"
      />
      <path
        d={`M -50 ${cliffY + 38} 
            Q ${cx * 0.55} ${cliffY + 12} ${cx * 1.15} ${cliffY + 36} 
            T ${vbWidth + 50} ${cliffY + 18} 
            L ${vbWidth + 50} ${vbHeight + 50} 
            L -50 ${vbHeight + 50} Z`}
        fill="#fb923c"
        opacity="0.2"
      />

      {/* 5. SOARING MOUNTAIN BIRDS IN DAWN FLIGHT (Ijduden) — Distinct, Elegant Arched Wings */}
      <g fill="#240c38">
        {/* High Leader Bird */}
        <path
          d={`M ${sunX - 175} ${sunY - 105} 
              Q ${sunX - 163} ${sunY - 117} ${sunX - 152} ${sunY - 107} 
              Q ${sunX - 141} ${sunY - 117} ${sunX - 129} ${sunY - 105} 
              Q ${sunX - 141} ${sunY - 110} ${sunX - 152} ${sunY - 105} 
              Q ${sunX - 163} ${sunY - 110} ${sunX - 175} ${sunY - 105} Z`}
          opacity="0.88"
        />
        {/* Companion Bird following below */}
        <path
          d={`M ${sunX - 130} ${sunY - 75} 
              Q ${sunX - 120} ${sunY - 85} ${sunX - 111} ${sunY - 77} 
              Q ${sunX - 102} ${sunY - 85} ${sunX - 92} ${sunY - 75} 
              Q ${sunX - 102} ${sunY - 79} ${sunX - 111} ${sunY - 75} 
              Q ${sunX - 120} ${sunY - 79} ${sunX - 130} ${sunY - 75} Z`}
          opacity="0.82"
        />
        {/* Distant Smaller Bird */}
        <path
          d={`M ${sunX - 88} ${sunY - 128} 
              Q ${sunX - 81} ${sunY - 136} ${sunX - 74} ${sunY - 129} 
              Q ${sunX - 67} ${sunY - 136} ${sunX - 60} ${sunY - 128} 
              Q ${sunX - 67} ${sunY - 131} ${sunX - 74} ${sunY - 128} 
              Q ${sunX - 81} ${sunY - 131} ${sunX - 88} ${sunY - 128} Z`}
          opacity="0.7"
        />
      </g>

      {/* 6. FOREGROUND ROCKY PROMONTORY (Natural Alpine Cliff Face - Pure Monolithic Mineral) */}
      {/* Deep Rock Body */}
      <path
        d={`M -50 ${cliffY + 20} 
            L ${heroX + 145} ${cliffY + 20} 
            L ${heroX + 180} ${cliffY + 40} 
            L ${heroX + 220} ${cliffY + 85} 
            L ${heroX + 260} ${vbHeight + 50} 
            L -50 ${vbHeight + 50} Z`}
        fill="url(#cliffStoneGrad)"
      />

      {/* Dawn Rim-Light along the Top Edge and Full Descent of the Cliff */}
      <path
        d={`M -50 ${cliffY + 20} 
            L ${heroX + 145} ${cliffY + 20} 
            L ${heroX + 180} ${cliffY + 40} 
            L ${heroX + 220} ${cliffY + 85} 
            L ${heroX + 260} ${vbHeight + 50}`}
        stroke="url(#cliffDawnRimGrad)"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />

      {/* 7. AQCIC (THE HERO) — Resolute on the Mountain Edge */}
      <g id="departure-hero" transform={`translate(${heroX}, ${cliffY - 75}) scale(${isPortrait ? 2.6 : 3.0})`}>
        {/* Clean, Grounded Contact Shadow precisely under the Hero Boots & Staff */}
        <ellipse cx="16" cy="41" rx="10" ry="1.8" fill="#000000" opacity="0.65" />
        <ellipse cx="28" cy="42" rx="2.5" ry="1" fill="#000000" opacity="0.55" />

        {/* Walking Staff (Aɛkkaz) planted forward into the rock */}
        <line x1="28" y1="5" x2="28" y2="42" stroke="#451a03" strokeWidth="2.4" strokeLinecap="round" />
        {/* Golden dawn reflection on right edge of staff */}
        <line x1="28.8" y1="5" x2="28.8" y2="42" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" />
        {/* Top Pommel (Boule du haut) */}
        <circle cx="28" cy="4" r="2.5" fill="#f59e0b" stroke="#78350f" strokeWidth="0.6" />
        <circle cx="29" cy="3.5" r="1" fill="#fef08a" />

        {/* Boots */}
        <rect x="9" y="37" width="5" height="4" fill="#0f172a" />
        <rect x="18" y="37" width="5" height="4" fill="#0f172a" />
        {/* Golden dawn catchlight on front boot */}
        <rect x="22" y="37" width="1" height="4" fill="#fde047" opacity="0.8" />

        {/* Dark Slate Trousers */}
        <rect x="10" y="28" width="4" height="9" fill="#1e293b" />
        <rect x="18" y="28" width="4" height="9" fill="#334155" />
        {/* Right leg dawn edge */}
        <rect x="21" y="28" width="1" height="9" fill="#fde047" opacity="0.4" />

        {/* Torso: Iconic Red Jacket */}
        <rect x="8" y="18" width="14" height="11" fill="#b91c1c" rx="1" />
        <rect x="13" y="18" width="4" height="11" fill="#f1f5f9" />
        {/* Dawn golden warmth on right torso/chest */}
        <rect x="19" y="18" width="3" height="11" fill="#f59e0b" opacity="0.7" rx="1" />

        {/* THE SACRED AMULET OF YAZ PROUDLY WORN ON CHEST */}
        <polygon points="14,21 16,19 18,21 17,23 15,23" fill="#065f46" stroke="#34d399" strokeWidth="0.8" />
        <text x="16" y="21.5" fill="#ffffff" fontSize="4.5" fontWeight="bold" textAnchor="middle" dominantBaseline="central">
          ⵣ
        </text>
        {/* Luminous gleam from the amulet */}
        <circle cx="16" cy="21.5" r="7" fill="url(#amuletHeroGleam)">
          <animate attributeName="r" values="6; 9; 6" dur="2s" repeatCount="indefinite" />
        </circle>

        {/* Left hand at side (in shadow) */}
        <rect x="5" y="20" width="3" height="6" fill="#991b1b" />
        <rect x="4.5" y="25" width="3.5" height="3" fill="#d97706" />

        {/* Right hand firmly gripping the walking staff (bathed in morning light) */}
        <rect x="21" y="20" width="5" height="4" fill="#dc2626" />
        <rect x="25" y="19" width="4" height="5" fill="#fde047" rx="1" />

        {/* Head & Face — Gaze fixed toward the sunrise & destination */}
        <rect x="9" y="10" width="12" height="9" fill="#f59e0b" rx="1" />
        {/* Face dawn rim on right side */}
        <rect x="18" y="10" width="3" height="9" fill="#fde047" rx="1" />
        {/* Eyes looking forward into the light */}
        <rect x="14" y="13" width="2" height="2" fill="#0f172a" />
        <rect x="18" y="13" width="2" height="2" fill="#0f172a" />

        {/* Fedora Hat with golden sunrise rim-light */}
        <rect x="7" y="4" width="16" height="6" fill="#0f172a" rx="1" />
        <rect x="5" y="8" width="20" height="3" fill="#0f172a" rx="0.5" />
        {/* Warm golden morning light striking hat brim & crown facing east */}
        <rect x="18" y="4" width="5" height="6" fill="#fde047" opacity="0.85" rx="1" />
        <rect x="18" y="8" width="7" height="3" fill="#fde047" opacity="0.9" rx="0.5" />
      </g>
    </svg>
  );
});
